import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';

// Layout Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Public Pages
import Home from './pages/Home';
import About from './pages/About';
import Products from './pages/Products';
import Contact from './pages/Contact';

// Auth Components
import SignIn from './components/Auth/SignIn';
import SignUp from './components/Auth/SignUp';

// Dashboard Components
import Dashboard from './components/Dashboard/Dashboard';
import MyVisits from './components/Visits/MyVisits';
import MySales from './components/Sales/MySales';
import MyWorkPlan from './components/WorkPlan/MyWorkPlan';
import Reports from './components/Reports/Reports';

// Admin Components
import AdminPanel from './components/Admin/AdminPanel';
import UserManagement from './components/Admin/UserManagement';
import ProductManagement from './components/Admin/ProductManagement';
import VendorManagement from './components/Admin/VendorManagement';

// Protected Route Component - Redirects to signin if not authenticated
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }
  
  return children;
};

// Admin Route Component - Redirects to dashboard if not admin
const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }
  
  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Layout wrapper for pages with Navbar and Footer
const AppLayout = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow">
        {children}
      </main>
      {!isAuthenticated && <Footer />}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        {/* Toast notifications */}
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
              borderRadius: '8px',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#1D9E75',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#DC2626',
                secondary: '#fff',
              },
            },
          }}
        />
        
        <Routes>
          {/* Public Routes - No authentication required */}
          <Route path="/" element={
            <AppLayout>
              <Home />
            </AppLayout>
          } />
          <Route path="/about" element={
            <AppLayout>
              <About />
            </AppLayout>
          } />
          <Route path="/products" element={
            <AppLayout>
              <Products />
            </AppLayout>
          } />
          <Route path="/contact" element={
            <AppLayout>
              <Contact />
            </AppLayout>
          } />
          
          {/* Auth Routes - No Navbar/Footer */}
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />

          {/* Protected Routes - Require authentication */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/visits" element={
            <ProtectedRoute>
              <AppLayout>
                <MyVisits />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/sales" element={
            <ProtectedRoute>
              <AppLayout>
                <MySales />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/workplan" element={
            <ProtectedRoute>
              <AppLayout>
                <MyWorkPlan />
              </AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/reports" element={
            <ProtectedRoute>
              <AppLayout>
                <Reports />
              </AppLayout>
            </ProtectedRoute>
          } />

          {/* Admin Routes - Require admin role */}
          <Route path="/admin" element={
            <AdminRoute>
              <AppLayout>
                <AdminPanel />
              </AppLayout>
            </AdminRoute>
          } />
          
          <Route path="/admin/users" element={
            <AdminRoute>
              <AppLayout>
                <UserManagement />
              </AppLayout>
            </AdminRoute>
          } />
          
          <Route path="/admin/products" element={
            <AdminRoute>
              <AppLayout>
                <ProductManagement />
              </AppLayout>
            </AdminRoute>
          } />
          
          <Route path="/admin/vendors" element={
            <AdminRoute>
              <AppLayout>
                <VendorManagement />
              </AppLayout>
            </AdminRoute>
          } />

          {/* Catch all - Redirect to home or dashboard based on auth */}
          <Route path="*" element={
            <Navigate to="/" replace />
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;